import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { glass } from "./portal";
import { useProject } from "@/lib/portal/useProject";
import { Globe, ShieldCheck, Server, Radar, AlarmClock } from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { it } from "date-fns/locale";

export const Route = createFileRoute("/_authenticated/portal/domains")({
  component: DomainsPage,
});

function DomainsPage() {
  const { data: project } = useProject();
  const { data: domain } = useQuery({
    queryKey: ["portal", "domain"],
    queryFn: async () => (await supabase.from("domains").select("*").limit(1).maybeSingle()).data,
    enabled: !!project,
  });
  const { data: health } = useQuery({
    queryKey: ["portal", "health"],
    queryFn: async () => (await supabase.from("health_scores").select("*").limit(1).maybeSingle()).data,
    enabled: !!project,
  });
  const daysToExpiry = domain?.expiry_date ? differenceInDays(new Date(domain.expiry_date), new Date()) : null;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Dominio & Hosting</h1>
        <p className="text-white/60 mt-1">Stato tecnico, SSL, DNS e scadenze.</p>
      </div>

      <div className={`${glass} p-6 flex items-center justify-between flex-wrap gap-4`}>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-500 grid place-items-center"><Globe className="h-6 w-6 text-black" /></div>
          <div>
            <div className="text-xl font-semibold">{domain?.domain_name ?? "—"}</div>
            <div className="text-sm text-white/60">Dominio principale collegato</div>
          </div>
        </div>
        {daysToExpiry !== null && (
          <div className="rounded-2xl bg-white/5 border border-white/10 px-4 py-2 flex items-center gap-2">
            <AlarmClock className="h-4 w-4 text-amber-300" />
            <div className="text-sm">Rinnovo tra <span className="font-semibold">{daysToExpiry}</span> giorni</div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Status icon={ShieldCheck} label="SSL" value={domain?.ssl_status ?? "—"} good={domain?.ssl_status === "active"} />
        <Status icon={Server} label="Hosting" value={domain?.hosting_status ?? "—"} good={domain?.hosting_status === "online"} />
        <Status icon={Radar} label="DNS" value={domain?.dns_status ?? "—"} good={domain?.dns_status === "ok"} />
        <Status icon={Globe} label="Uptime 30gg" value={health?.uptime_percent ? `${health.uptime_percent}%` : "—"} good={(health?.uptime_percent ?? 0) > 99} />
      </div>

      <div className={`${glass} p-6 grid grid-cols-1 sm:grid-cols-3 gap-4`}>
        <Info label="Scadenza dominio" value={domain?.expiry_date ? format(new Date(domain.expiry_date), "d MMM yyyy", { locale: it }) : "—"} />
        <Info label="Scadenza SSL" value={health?.ssl_expiry ? format(new Date(health.ssl_expiry), "d MMM yyyy", { locale: it }) : "—"} />
        <Info label="Rinnovo automatico" value={domain?.auto_renew ? "Attivo" : "Disattivo"} />
      </div>
    </div>
  );
}

function Status({ icon: Icon, label, value, good }: { icon: typeof Globe; label: string; value: string; good: boolean }) {
  return (
    <div className={`${glass} p-5`}>
      <Icon className={`h-5 w-5 ${good ? "text-emerald-300" : "text-amber-300"}`} />
      <div className="mt-3 text-lg font-semibold capitalize">{value}</div>
      <div className="text-xs text-white/50 mt-0.5">{label}</div>
    </div>
  );
}
function Info({ label, value }: { label: string; value: string }) {
  return <div><div className="text-xs text-white/40">{label}</div><div className="text-sm font-medium mt-1">{value}</div></div>;
}
